import React from 'react';
import {
  Form,
  Input,
  DatePicker,
  InputNumber,
  Button,
  message,
  Divider,
  Typography,
  Row,
  Col,
  Select
} from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useDocumentGeneration from '../../hooks/useDocumentGeneration';
import { colors, spacing, radius, typography } from '../../styles/monochromeTheme';

const { Text } = Typography;

// Monochrome styles
const styles = {
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xxl,
  },
  card: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xxl,
  },
  primaryBtn: {
    height: '52px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    border: 'none',
    color: colors.textInverse,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  infoBox: {
    padding: spacing.lg,
    backgroundColor: colors.bgTertiary,
    borderRadius: radius.lg,
    border: `1px solid ${colors.borderSubtle}`,
  },
};

function JetskiDocumentGenerator() {
  const [form] = Form.useForm();
  const { generateDoc, loading } = useDocumentGeneration();

  const calculateTotal = () => {
    const jetskiNumber = form.getFieldValue('jetski_number') || 0;
    const jetskiPrice = form.getFieldValue('jetski_price') || 0;
    const flyboardPrice = form.getFieldValue('flyboard_price') || 0;
    const total = (jetskiNumber * jetskiPrice) + flyboardPrice;
    form.setFieldsValue({ total_price: total });
  };

  const handleGenerateContrat = async (values) => {
    try {
      const charterId = `JETSKI-${dayjs().format('YYYYMMDD-HHmmss')}`;

      // Handle optional jetski data - only include if jetski_number > 0
      const hasJetski = values.jetski_number && values.jetski_number > 0;

      const documentData = {
        charter_date: values.charter_date ? dayjs(values.charter_date).format('DD/MM/YYYY') : '',
        spot: values.spot || 'SBH',
        jetski_number: hasJetski ? `${values.jetski_number} jetski${values.jetski_number > 1 ? 's' : ''}` : '',
        jetski_duration: hasJetski ? (values.jetski_duration || '') : '',
        charterer: values.charterer || '',
        client_name: values.client_name || '',
        jetski_price: hasJetski && values.jetski_price ? `${values.jetski_price}€` : '',
        flyboard_duration: values.flyboard_duration || '',
        flyboard_price: values.flyboard_price ? `${values.flyboard_price}€` : '',
        total_price: values.total_price ? `${values.total_price}€` : '',
      };

      await generateDoc('Contrat', documentData, charterId, 'jetski');
      form.resetFields();
    } catch (error) {
      console.error('Error:', error);
      let errorMessage = 'Erreur lors de la génération du contrat';
      if (error && error.message) errorMessage += ': ' + error.message;
      message.error({ content: errorMessage, duration: 10 });
    }
  };

  return (
    <div style={styles.page}>
      <style>
        {`
          .ant-form-item-label > label { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textSecondary} !important; text-transform: uppercase; letter-spacing: 0.5px; }
          .ant-input, .ant-select-selector, .ant-picker, .ant-input-number { border-radius: ${radius.md} !important; border-color: ${colors.borderMedium} !important; }
          .ant-input:focus, .ant-select-selector:focus, .ant-picker-focused { border-color: ${colors.bgDark} !important; box-shadow: 0 0 0 2px rgba(0,0,0,0.1) !important; }
          .ant-divider-inner-text { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textMuted} !important; text-transform: uppercase; letter-spacing: 1px; }
        `}
      </style>

      <div style={styles.card}>
        <Divider style={{ marginTop: 0 }}>Contract Information</Divider>

        <Form form={form} layout="vertical" onFinish={handleGenerateContrat} initialValues={{ spot: 'SBH' }}>
          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item name="charter_date" label="Date" rules={[{ required: true, message: 'Requis' }]}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%', height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="spot" label="Destination (Spot)" initialValue="SBH" rules={[{ required: true, message: 'Requis' }]}>
                <Select style={{ height: '44px' }}>
                  <Select.Option value="SBH">SBH</Select.Option>
                  <Select.Option value="AXA">AXA</Select.Option>
                  <Select.Option value="SXM">SXM</Select.Option>
                  <Select.Option value="Tintamarre">Tintamarre</Select.Option>
                  <Select.Option value="Pinel">Pinel</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item name="charterer" label="Charterer" rules={[{ required: true, message: 'Requis' }]}>
                <Input style={{ height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="client_name" label="Client Name" rules={[{ required: true, message: 'Requis' }]}>
                <Input style={{ height: '44px' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }}>Jet-ski Details (Optional)</Divider>

          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item name="jetski_number" label="Number of Jet-ski">
                <InputNumber style={{ width: '100%', height: '44px' }} min={0} onChange={calculateTotal} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="jetski_duration" label="Duration">
                <Input placeholder="2 heures" style={{ height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="jetski_price" label="Price per Jet-ski (€)">
                <InputNumber style={{ width: '100%', height: '44px' }} min={0} onChange={calculateTotal} placeholder="150" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }}>Flyboard Details</Divider>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item name="flyboard_duration" label="Flyboard Duration">
                <Input placeholder="30 minutes" style={{ height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="flyboard_price" label="Price per Flyboard (€)">
                <InputNumber style={{ width: '100%', height: '44px' }} min={0} onChange={calculateTotal} placeholder="200" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }}>Total</Divider>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item name="total_price" label="Total Price (€)" rules={[{ required: true, message: 'Requis' }]}>
                <InputNumber style={{ width: '100%', height: '44px', fontWeight: 700 }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: 'center', marginTop: spacing.xl }}>
            <Button type="primary" htmlType="submit" icon={<FileTextOutlined />} size="large" loading={loading} style={styles.primaryBtn}>
              Generate Contract
            </Button>
          </div>
        </Form>

        <Divider style={{ margin: `${spacing.xl} 0` }} />

        <div style={styles.infoBox}>
          <Text strong>Workflow:</Text>
          <div style={{ marginTop: spacing.sm, lineHeight: '1.6', color: colors.textTertiary, fontSize: typography.fontSize.sm }}>
            After generation, the contract appears in <strong>Document List</strong>.
            You can then generate Confirmation and Invoice from that page.
          </div>
        </div>
      </div>
    </div>
  );
}

export default JetskiDocumentGenerator;
